import React, { useState, useRef } from 'react';
import { Upload, FileText, AlertCircle, MessageSquare } from 'lucide-react';

const Landing = ({ onFileUpload, onStartDirectChat }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  // Taglines that will alternate
  const taglines = [
    "Analyze. Comply. Excel.",
    "Smart Medical Review.",
    "AI Document Intelligence.",
  ];
  
  const [currentTagline, setCurrentTagline] = useState(0);
  
  // Change tagline every 3 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [taglines.length]);
  
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndUpload(e.dataTransfer.files[0]);
    }
  };
  
  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndUpload(e.target.files[0]);
    }
  };
  
  // Trigger file input click
  const handleButtonClick = () => {
    fileInputRef.current.click();
  };
  
  // Validate file and upload
  const validateAndUpload = (file) => {
    // Reset error
    setError('');
    
    // Check file type
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }
    
    // Check file size (limit to 10 MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size should not exceed 10 MB');
      return;
    }
    
    // Pass file to parent component
    onFileUpload(file);
  };
  
  return (
    <div className="landing-gradient flex flex-col items-center justify-center min-h-screen p-4 relative">
      <div className="absolute top-0 left-0 w-full p-4">
        <h1 className="text-2xl font-bold text-primary">AI Medical Compliance</h1>
      </div>
      
      {/* Main content */}
      <div className="z-10 max-w-2xl w-full">
        <h1 className="text-4xl font-bold text-center mb-4">
          Your Compliance Buddy
        </h1>
        
        <div className="text-center mb-8 h-8">
          <p className="text-xl text-primary animate-pulse-slow">
            {taglines[currentTagline]}
          </p>
        </div>
        
        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            isDragging 
              ? 'border-primary bg-dark-lighter' 
              : 'border-gray-600 hover:border-primary-light hover:bg-dark'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileText size={48} className="mx-auto mb-4 text-primary" />
          <h2 className="text-xl font-semibold mb-2">
            Upload your PDF document or start a direct chat
          </h2>
          <p className="text-gray-400 mb-6">
            Drag and drop your file here, or choose an option below
          </p>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleButtonClick}
              className="bg-primary hover:bg-primary-dark transition-colors duration-200 text-white font-medium py-3 px-6 rounded-lg flex items-center justify-center"
            >
              <Upload size={18} className="mr-2" />
              Select PDF File
            </button>
            
            <button
              onClick={onStartDirectChat}
              className="bg-primary hover:bg-primary-dark transition-colors duration-200 text-white font-medium py-3 px-6 rounded-lg flex items-center justify-center"
            >
              <MessageSquare size={18} className="mr-2" />
              Start Direct Chat
            </button>
          </div>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept="application/pdf"
            className="hidden"
          />
          
          {error && (
            <div className="mt-4 text-red-500 flex items-center justify-center">
              <AlertCircle size={16} className="mr-2" />
              {error}
            </div>
          )}
        </div>
        
        <p className="text-center mt-6 text-gray-400 text-sm">
          Your document will be securely processed.
        </p>
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-0 left-0 w-full p-4 text-center">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} AI by DNA
        </p>
      </div>
    </div>
  );
};

export default Landing;